import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/* Popover em portal + posição clamped à viewport (mesmo padrão do TimePicker):
   dentro do BottomSheet um dropdown absoluto seria cortado pelo overflow do painel. */
const POPOVER_WIDTH = 340;
const VIEWPORT_MARGIN = 12;
const GAP = 6;
const POPOVER_HEIGHT = 348;
const POPOVER_MIN_HEIGHT = 244;

interface DatePickerProps {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
// Semana começa no domingo (igual ao protótipo).
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WD_FULL = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

function parseISO(s: string): Date {
  const parts = s.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}
function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function fmtBR(s: string) {
  const d = parseISO(s);
  const wd = WD_FULL[d.getDay()];
  return `${wd} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function DatePicker({ value, onChange, className }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const selected = useMemo(() => (value ? parseISO(value) : new Date()), [value]);
  const [viewMonth, setViewMonth] = useState(
    () => new Date(selected.getFullYear(), selected.getMonth(), 1),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Abre embaixo por padrão; sobe quando não cabe e há mais espaço acima.
  // A altura é limitada ao espaço livre — a grade de dias rola por dentro.
  useEffect(() => {
    if (!open || !rootRef.current) return;
    function calc() {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      // visualViewport reflete o teclado aberto no iOS; innerHeight não.
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const width = Math.min(rect.width, POPOVER_WIDTH);
      let left = rect.left;
      if (left + width > vw - VIEWPORT_MARGIN) left = vw - width - VIEWPORT_MARGIN;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

      const spaceBelow = vh - rect.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
      const placeAbove = spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow;
      const avail = placeAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(POPOVER_MIN_HEIGHT, Math.min(POPOVER_HEIGHT, avail));
      const top = placeAbove
        ? Math.max(VIEWPORT_MARGIN, rect.top - GAP - maxHeight)
        : rect.bottom + GAP;
      setPos({ top, left, width, maxHeight });
    }
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    window.visualViewport?.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
      window.visualViewport?.removeEventListener('resize', calc);
    };
  }, [open]);

  // Ao abrir, sincroniza o mês visível com o valor selecionado.
  useEffect(() => {
    if (open && value) {
      const d = parseISO(value);
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [open, value]);

  // getDay() = 0 (dom) … 6 (sáb). Semana começa no domingo.
  const firstDayIdx = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const todayISO = toISO(new Date());
  const cells: Array<number | null> = [
    ...Array.from<unknown, null>({ length: firstDayIdx }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  function pick(day: number) {
    const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
    onChange(toISO(d));
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      {/* Trigger — mono uppercase, borda escura quando aberto */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          'w-full inline-flex items-center justify-between rounded-lg border bg-card px-3.5 py-3 font-mono text-xs font-semibold uppercase tracking-wider transition-colors',
          open ? 'border-foreground' : 'border-border',
          value ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span>{value ? fmtBR(value) : 'Selecionar data'}</span>
        <CalendarDays className="h-[15px] w-[15px] text-muted-foreground" />
      </button>

      {open && pos && typeof document !== 'undefined' &&
        createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[70] flex flex-col rounded-xl border border-border bg-card p-3 shadow-lift"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
            animation: 'u-rise 0.2s var(--ease-underline)',
          }}
        >
          {/* Cabeçalho de navegação de mês */}
          <div className="mb-2 flex shrink-0 items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
              }
              className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-surface-2 text-foreground transition-colors hover:bg-muted"
            >
              <ChevronLeft className="h-[15px] w-[15px]" />
            </button>
            <span className="font-mono text-xs font-bold uppercase tracking-wider">
              {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() =>
                setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
              }
              className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-surface-2 text-foreground transition-colors hover:bg-muted"
            >
              <ChevronRight className="h-[15px] w-[15px]" />
            </button>
          </div>

          {/* Cabeçalho dos dias da semana */}
          <div className="mb-1 grid shrink-0 grid-cols-7 gap-0.5">
            {WEEKDAYS.map((w, i) => (
              <div
                key={i}
                className="text-center font-mono text-[10px] text-muted-foreground py-1"
              >
                {w}
              </div>
            ))}
          </div>

          {/* Grade de dias — rola por dentro se o espaço na tela for curto */}
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-7 gap-0.5 overflow-y-auto">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const iso = toISO(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
              const isSel = iso === value;
              const isToday = !isSel && iso === todayISO;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(d)}
                  className={cn(
                    'h-9 inline-flex items-center justify-center rounded-md text-[13px] font-medium transition-colors',
                    isSel
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted',
                    isToday && 'border border-foreground',
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
